import React from 'react';
import styled from 'styled-components';

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2eaf4;
  border-radius: 14px;
  padding: 20px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(14, 165, 233, 0.06);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #1a2744;
  margin: 0;
  flex: 1;
`;

const CardDescription = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 180px;
`;

const RegenerateButton = styled.button<{ $loading?: boolean }>`
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: #eef4ff;
  border: 1px solid #e2eaf4;
  border-radius: 6px;
  cursor: ${(p) => (p.$loading ? 'wait' : 'pointer')};
  opacity: ${(p) => (p.$loading ? 0.8 : 1)};
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: #dbeafe;
    border-color: #bfdbfe;
    color: #1a2744;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

export interface DashboardCardRegenerateProps {
  label?: string;
  onClick: () => void;
  loading?: boolean;
}

export interface DashboardCardProps {
  title: string;
  description?: string;
  /** Optional Regenerate action: shows a button in the card header. */
  regenerate?: DashboardCardRegenerateProps;
  children: React.ReactNode;
}

/**
 * Reusable dashboard card: title, optional description, optional per-card Regenerate button, and content.
 * Use for correlation plots, stats, or any card-style block on the dashboard.
 */
const DashboardCard: React.FC<DashboardCardProps> = ({ title, description, regenerate, children }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {regenerate && (
          <RegenerateButton
            type="button"
            onClick={regenerate.onClick}
            disabled={regenerate.loading}
            $loading={regenerate.loading}
          >
            {regenerate.loading ? 'Regenerating…' : regenerate.label ?? 'Regenerate'}
          </RegenerateButton>
        )}
      </CardHeader>
      {description != null && description !== '' && <CardDescription>{description}</CardDescription>}
      <CardContent>{children}</CardContent>
    </Card>
  );
};

export default DashboardCard;
